import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  CardBody,
  Button,
  Table,
  Input,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../axiosInstance";
import { URL } from "../../api.jsx";

const AllAssignedCases = () => {
  const callerId = localStorage.getItem("userId");
  const navigate = useNavigate();

  /* ================= STATES ================= */
  const [cases, setCases] = useState([]);

  // FILTER
  const [filterType, setFilterType] = useState("");
  const [filterValue, setFilterValue] = useState("");

  // ASSIGN CASES MODAL
  const [assignModal, setAssignModal] = useState(false);
  const [callersList, setCallersList] = useState([]);
  const [selectedCaller, setSelectedCaller] = useState("");
  const [loanIdsInput, setLoanIdsInput] = useState("");
  const [assigning, setAssigning] = useState(false);

  // BULK ASSIGN (EXCEL UPLOAD)
  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkFileInputRef = useRef(null);

  // PAGINATION
  const [page, setPage] = useState(0);
  const [size] = useState(10);

  // LOADING
  const [loading, setLoading] = useState(false);

  /* ================= API ================= */
  const fetchAllAssignedCases = async (pageNo = 0) => {
    try {
      setLoading(true);

      const params = {
        callerId,
        admin: 1,
        page: pageNo,
        size,
      };

      if (filterType === "loan_id") params.loanId = filterValue;
      if (filterType === "phone") params.phoneNumber = filterValue;

      const res = await axiosInstance.get(URL.CASES_ASSIGNED, { params });

      let list = [];
      if (Array.isArray(res.data)) list = res.data;
      else if (Array.isArray(res.data?.content)) list = res.data.content;

      setCases(list);
      setPage(pageNo);
    } catch (e) {
      toast.error("Failed to load all assigned cases");
    } finally {
      setLoading(false);
    }
  };

  /* ================= INIT ================= */
  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    fetchAllAssignedCases(0);
  }, []);

  /* ================= FILTER HANDLERS ================= */
  const handleSearch = () => fetchAllAssignedCases(0);

  const handleReset = () => {
    setFilterType("");
    setFilterValue("");
    fetchAllAssignedCases(0);
  };

  /* ================= ASSIGN CASES ================= */
  const openAssignModal = async () => {
    setAssignModal(true);
    setSelectedCaller("");
    setLoanIdsInput("");
    try {
      const res = await axiosInstance.get(URL.CALLER_LIST, {
        params: { groupId: 250 },
      });
      setCallersList(res.data || []);
    } catch {
      toast.error("Failed to load callers");
    }
  };

  const handleManualAssign = async () => {
    if (!selectedCaller) {
      toast.error("Please select a caller");
      return;
    }
    if (!loanIdsInput.trim()) {
      toast.error("Please enter loan IDs");
      return;
    }

    const loanIds = loanIdsInput
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean)
      .map(Number);

    if (loanIds.some(isNaN)) {
      toast.error("Invalid loan IDs — use comma-separated numbers");
      return;
    }

    try {
      setAssigning(true);
      const res = await axiosInstance.post(
        `${URL.MANUAL_ASSIGN}?callerId=${selectedCaller}`,
        { loanIds }
      );
      const d = res.data;
      toast.success(
        `Assigned: ${d.newlyAssigned || 0} new, ${d.reassigned || 0} reassigned` +
          (d.loansNotFound?.length ? `, Not found: ${d.loansNotFound.join(", ")}` : "")
      );
      setAssignModal(false);
      fetchAllAssignedCases(page);
    } catch {
      toast.error("Manual assign failed");
    } finally {
      setAssigning(false);
    }
  };

  /* ================= BULK ASSIGN (EXCEL) ================= */
  const handleBulkAssignExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      setBulkUploading(true);
      const res = await axiosInstance.post(URL.BULK_ASSIGN_EXCEL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = res.data || {};
      toast.success(
        `Callers: ${d.callersProcessed || 0}, New: ${d.newlyAssigned || 0}, Reassigned: ${d.reassigned || 0}` +
          (d.loansNotFound?.length ? `, Not found: ${d.loansNotFound.join(", ")}` : "") +
          (d.failedRows?.length ? `, Failed rows: ${d.failedRows.length}` : "")
      );
      fetchAllAssignedCases(page);
    } catch {
      toast.error("Bulk Excel assignment failed");
    } finally {
      setBulkUploading(false);
      if (bulkFileInputRef.current) bulkFileInputRef.current.value = "";
    }
  };

  /* ================= UI ================= */
  return (
    <>
      {/* ================= FILTER ================= */}
      <Card className="mb-3 p-3">
        <div className="d-flex gap-2 align-items-end flex-wrap justify-content-between">
          <div className="d-flex gap-2 align-items-end flex-wrap">
            <div>
              <Label>Filter Type</Label>
              <select
                className="form-select form-select-sm"
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setFilterValue("");
                }}
              >
                <option value="">Select</option>
                <option value="loan_id">Loan ID</option>
                <option value="phone">Phone Number</option>
              </select>
            </div>

            {filterType && (
              <div>
                <Label>
                  {filterType === "loan_id" ? "Loan ID" : "Phone Number"}
                </Label>
                <Input
                  bsSize="sm"
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                />
              </div>
            )}

            <Button size="sm" color="info" onClick={handleSearch}>
              Search
            </Button>

            <Button size="sm" color="secondary" onClick={handleReset}>
              Reset
            </Button>
          </div>

          {/* ASSIGN & BULK ASSIGN BUTTONS — admin-only page */}
          <div className="d-flex flex-column gap-1">
            <Button size="sm" color="primary" onClick={openAssignModal}>
              Assign Cases
            </Button>

            <input
              type="file"
              accept=".xlsx,.xls"
              ref={bulkFileInputRef}
              style={{ display: "none" }}
              onChange={handleBulkAssignExcel}
            />
            <Button
              size="sm"
              color="warning"
              disabled={bulkUploading}
              onClick={() => bulkFileInputRef.current?.click()}
            >
              {bulkUploading ? "Uploading..." : "Bulk Assign (Excel)"}
            </Button>
          </div>
        </div>
      </Card>

      {/* ================= TABLE ================= */}
      <Card>
        <CardBody>
          {loading ? (
            <Spinner />
          ) : (
            <Table bordered hover size="sm" responsive className="text-nowrap">
              <thead>
                <tr>
                  <th>Loan ID</th>
                  <th>Customer ID</th>
                  <th>Customer Name</th>
                  <th>Fintech Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Overdue Outstanding</th>
                  <th>Aging</th>
                  <th>Caller Name</th>
                  <th>Status</th>
                  <th>Assigned Date</th>
                  <th>Last Update Date</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {cases.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="text-center">
                      No records found
                    </td>
                  </tr>
                ) : (
                  cases.map((c, i) => (
                    <tr key={i}>
                      <td>{c.loanid}</td>
                      <td>{c.customerid}</td>
                      <td>{c.customername}</td>
                      <td>{c.fintech_name}</td>
                      <td>{c.phonenumber}</td>
                      <td>{c.email}</td>
                      <td>{c.overdueoutstanding}</td>
                      <td>{c.aging}</td>
                      <td>{c.callername}</td>
                      <td>
                        <span className="badge bg-warning text-dark">
                          {c.status}
                        </span>
                      </td>
                      <td>{c.assigneddate}</td>
                      <td>{c.lastUpdated}</td>
                      <td>
                        <Button
                          size="sm"
                          color="info"
                          outline
                          onClick={() =>
                            navigate(`/CustomerView?customer_id=${c.customerid}`)
                          }
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}

          {/* ================= PAGINATION ================= */}
          <div className="d-flex justify-content-end gap-2 mt-2">
            <Button
              size="sm"
              disabled={page === 0}
              onClick={() => fetchAllAssignedCases(page - 1)}
            >
              Prev
            </Button>

            <span className="align-self-center">Page {page + 1}</span>

            <Button
              size="sm"
              disabled={cases.length < size}
              onClick={() => fetchAllAssignedCases(page + 1)}
            >
              Next
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* ================= ASSIGN CASES MODAL ================= */}
      <Modal isOpen={assignModal} toggle={() => setAssignModal(false)}>
        <ModalHeader toggle={() => setAssignModal(false)}>
          Assign Cases
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label>Select Caller</Label>
            <Input
              type="select"
              bsSize="sm"
              value={selectedCaller}
              onChange={(e) => setSelectedCaller(e.target.value)}
            >
              <option value="">-- Select Caller --</option>
              {callersList.map((c) => (
                <option key={c.user_id} value={c.user_id}>
                  {c.user_id} - {c.name}
                </option>
              ))}
            </Input>
          </div>

          <div className="mb-3">
            <Label>Loan IDs (comma separated)</Label>
            <Input
              type="textarea"
              bsSize="sm"
              rows={3}
              placeholder="e.g. 101, 102, 103"
              value={loanIdsInput}
              onChange={(e) => setLoanIdsInput(e.target.value)}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" color="secondary" onClick={() => setAssignModal(false)}>
            Cancel
          </Button>
          <Button size="sm" color="primary" disabled={assigning} onClick={handleManualAssign}>
            {assigning ? "Assigning..." : "Assign"}
          </Button>
        </ModalFooter>
      </Modal>

      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
      />
    </>
  );
};

export default AllAssignedCases;
